import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import ModalTemplate from "@/shared/components/ModalTemplate/ModalTemplate.vue";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import {
  FORM_CONSTANTS,
  FORM_MESSAGES,
  FORM_VALIDATORS
} from "@/shared/constants/FormConstants";
import { GENERAL_CONSTANTS } from "@/shared/constants/GeneralConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { Response } from "@/shared/models/Response";
import store from "@/store";
import { Http } from "@status/codes";
import { AxiosError } from "axios";
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import {
  DASHBOARD_TEMPLATE_EVENTS,
  TAG
} from "../../constants/DashboardTemplatesConstants";
import { CollectionFilter } from "../../models/CollectionFilter";
import { DashboardTemplate } from "../../models/DashboardTemplate";
import { Field } from "../../models/Field";
import {
  CollectionsOptions,
  SectionOfDashboard
} from "../../models/SectionOfDashboard";
import { ViewsConfigResponse } from "../../models/viewsConfigResponse";
import { BqDashboardService } from "../../services/BqDashboardService";
import DashboardPreview from "../Preview/DashboardPreview.vue";
import SectionsOfdashboard from "../SectionsOFDashboard/SectionsOfdashboard.vue";
import cloneDeep from "lodash/cloneDeep";
import { DynamicSelectConfig } from "@/shared/components/DynamicSelect/model/DynamicSelectConfig";
import { IndicatorOrTag } from "../../models/IndicatorOrTag";

@Component({
  name: "SaveDashboardTemplate",
  components: {
    DynamicSelect,
    SectionsOfdashboard,
    DashboardPreview,
    ModalTemplate
  },
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard"
    })
  }
})
export default class SaveDashboardTemplate extends Vue {
  @Inject() bqDashboardService!: BqDashboardService;
  @Prop() dashboardTemplateId?: string;

  sectionsOfDashboardStore!: Array<SectionOfDashboard>;

  sectionsOfDashboardLocalState: number[] = [];
  isLoadingSections = false;
  get loadingProgress(): number {
    const total = this.sectionsOfDashboardStore.length;
    if (!total) return 0;
    return (this.sectionsOfDashboardLocalState.length / total) * 100;
  }
  addItem = GENERAL_CONSTANTS.ICONS.ADD;
  primaryBtnClass = STYLE_CLASSES.PRIMARY_BTN;
  roundBtnClass = STYLE_CLASSES.TERTIARY_ROUND_BTN;
  collectionsData: ViewsConfigResponse[] = [];
  iconBtnClass = STYLE_CLASSES.ICON_BTN;
  SAVE_BTN = FORM_CONSTANTS.BUTTON.SAVE;
  PAGE_START = FORM_CONSTANTS.BUTTON.PAGE_START;
  CANCEL_BTN = FORM_CONSTANTS.BUTTON.CANCEL;
  PREVIEW_BTN = FORM_CONSTANTS.BUTTON.PREVIEW;
  previewDialog = false;
  dashboardTemplateForm = false;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  state = true;
  requiredField = [
    FORM_VALIDATORS.REQUIRED_FIELD,
    FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
  ];
  dashboardTemplateName = "";
  switchClass = STYLE_CLASSES.SWITCH;
  modalDialog = false;
  modalText = "";
  modalTitle = "";
  isEditing = false;
  preloaded = false;
  templateTypeModel: DynamicSelectData[] = [];
  templateTypeSelectConfig: DynamicSelectConfig = {
    multiple: false,
    default: [],
    chips: true,
    notFound: false,
    listAll: [
      { id: "General", name: "General" },
      { id: "Paciente", name: "Paciente" }
    ],
    filled: false,
    messageLabel: "Tipo de plantilla",
    hideDetails: true
  };
  async mounted() {
    this.cleanData();
    await this.getCollectionsOptions();
    if (this.dashboardTemplateId) {
      this.isEditing = true;
      this.preloadData();
      return;
    }
    this.preloaded = true;
  }

  @Watch("sectionsOfDashboardStore", { deep: true })
  changedSectionsOfDashboardStore() {
    if (this.isLoadingSections) return;

    if (
      this.sectionsOfDashboardStore.length >
      this.sectionsOfDashboardLocalState.length
    ) {
      this.sectionsOfDashboardLocalState.push(
        (this.sectionsOfDashboardLocalState[
          this.sectionsOfDashboardLocalState.length - 1
        ] ?? 0) + 1
      );
    }
  }

  async addSectionsProgressively() {
    this.isLoadingSections = true;
    this.sectionsOfDashboardLocalState = [];

    for (let i = 0; i < this.sectionsOfDashboardStore.length; i++) {
      this.sectionsOfDashboardLocalState.push(i);
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.$nextTick();
    }

    this.isLoadingSections = false;
  }

  @Watch("dashboardTemplateId")
  changedTemplateId() {
    this.cleanData();
    this.isEditing = true;
    this.preloadData();
  }

  async addSectionOfDashboard() {
    store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_SECTIONS_OF_DASHBOARD, {
      name: "",
      collections: [
        {
          collection: [{ id: "", name: "", fields: [] }],
          filters: [],
          groupBy: [],
          seeBy: [],
          chartName: "",
          query: {},
          chartData: {}
        }
      ],
      tags: [],
      indicators: [],
      collectionsOptions: cloneDeep(this.collectionsData)
    });

    await this.$nextTick();

    const sections = document.querySelectorAll(".d-flex.justify-center");
    const lastSection = sections[sections.length - 1];

    if (lastSection) {
      lastSection.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }

  async getCollectionsOptions() {
    try {
      const viewsConfig = await this.bqDashboardService.getViesConfig();

      if (viewsConfig.data) {
        this.collectionsData = viewsConfig.data;
        return;
      }
      Snackbar.popInfo(viewsConfig.message);
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async buildDataToSaveTemplate(templateData: DashboardTemplate) {
    const dataToSave: any = {
      name: templateData.dashboardTemplateName,
      type: this.templateTypeModel[0].id,
      state: templateData.state,
      sections: []
    };
    templateData.sectionsOfDashboard.forEach(section => {
      const itemSection: any = {
        name: section.name,
        layout: section.layout,
        collections: [],
        tags: [],
        indicators: []
      };
      for (const itemCollection of section.collections) {
        if (Array.isArray(itemCollection.seeBy)) {
          itemCollection.seeBy =
            itemCollection.seeBy.length > 0 ? itemCollection.seeBy[0] : "";
        }

        let groupBy: DynamicSelectData[] | DynamicSelectData | string[] = [];
        if (Array.isArray(itemCollection.groupBy)) {
          groupBy = (itemCollection.groupBy as DynamicSelectData[]).map(
            (element: DynamicSelectData | string) => {
              if (typeof element === "string") return element;
              return element.id;
            }
          );
        }

        const itemCollectionStore = itemCollection.collection[0];
        itemSection.collections.push({
          id: itemCollectionStore.id,
          name: itemCollectionStore.name,
          chart: {
            chartName: itemCollection.chartName,
            groupBy: groupBy,
            seeBy: itemCollection.seeBy,
            rangeSize: itemCollection.rangeSize
          },
          filters: itemCollection.filters,
          query: itemCollection.query,
          chartType: itemCollection.chartType,
          chartColor: itemCollection.chartColor,
          layout: itemCollection.layout
        });
      }
      if (section.tags) {
        for (const itemTag of section.tags) {
          if (itemTag.tagOrIndicator == TAG) {
            const newTag: IndicatorOrTag = {
              indicatorOrTagName: itemTag.indicatorOrTagName,
              operation: Array.isArray(itemTag.operation)
                ? (itemTag.operation[0] as { id: string; name: string }).id
                : itemTag.operation,
              field: itemTag.fieldToOperate
                ? itemTag.fieldToOperate[0].id
                : itemTag.field,
              view: itemTag.fieldToOperate
                ? itemTag.fieldToOperate[0].collection?.id
                : itemTag.view,
              show: itemTag.show,
              tagOrIndicator: itemTag.tagOrIndicator,
              color: itemTag.color,
              query: itemTag.query,
              value: 0,
              textColor: itemTag.textColor,
              edgeColor: itemTag.edgeColor,
              layout: itemTag.layout,
              selectorFieldToOperate: itemTag.selectorFieldToOperate
            };

            if (itemTag.fieldToOperate) {
              newTag.fieldToOperate = itemTag.fieldToOperate;
            }

            itemSection.tags.push(newTag);
          }
        }

        for (const indicator of section.indicators) {
          itemSection.indicators.push({
            indicatorOrTagName: indicator.indicatorOrTagName,
            formulaToCreateIndicator: indicator.formulaToCreateIndicator?.map(
              (item: any) => {
                if (Array.isArray(item)) {
                  return item[0];
                }
                return item;
              }
            ),
            show: indicator.show,
            tagOrIndicator: indicator.tagOrIndicator,
            color: indicator.color,
            edgeColor: indicator.edgeColor,
            textColor: indicator.textColor,
            layout: indicator.layout
          });
        }
      }
      dataToSave.sections.push(itemSection);
    });
    return dataToSave;
  }

  async saveDashboardTemplate() {
    const dashboardTemplate = new DashboardTemplate();
    dashboardTemplate.dashboardTemplateName = this.dashboardTemplateName;
    dashboardTemplate.state = this.state;
    dashboardTemplate.sectionsOfDashboard = this.sectionsOfDashboardStore;
    const dataFormated = await this.buildDataToSaveTemplate(dashboardTemplate);

    let response: Response<string | void>;
    if (this.isEditing) {
      dataFormated.id = this.dashboardTemplateId;
      response = await this.bqDashboardService.updateTemplate(dataFormated);
    } else {
      response = await this.bqDashboardService.saveTemplate(dataFormated);
    }
    if (response.code === Http.Ok) {
      Snackbar.popSuccess(response.message);
      this.$emit(DASHBOARD_TEMPLATE_EVENTS.TEMPLATE_SAVED, {
        id: response.data,
        name: dashboardTemplate.dashboardTemplateName,
        state: dashboardTemplate.state
      });
    } else {
      Snackbar.popError(response.message);
    }
  }

  openPreviewDialog() {
    this.previewDialog = true;
  }

  confirmDialog(isSave: boolean) {
    if (isSave) {
      this.modalTitle = FORM_MESSAGES.SAVE_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.SAVE_MODAL.MESSAGE;
    } else {
      this.modalTitle = FORM_MESSAGES.CANCEL_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.CANCEL_MODAL.MESSAGE;
    }

    this.modalDialog = true;
  }

  async handleModalAccept() {
    this.modalDialog = false;

    if (this.modalTitle === FORM_MESSAGES.SAVE_MODAL.TITLE) {
      await this.saveDashboardTemplate();
    } else {
      this.$router.go(-1);
    }
  }

  handleModalCancel() {
    this.modalDialog = false;
  }

  async preloadData() {
    const response = await this.bqDashboardService.getTemplateById(
      this.dashboardTemplateId!
    );
    if (response.code === Http.Found) {
      this.countCollections(response.data.sections);
      this.dashboardTemplateName = response.data.name;
      this.state = response.data.state;
      this.templateTypeModel = [
        { id: response.data.type, name: response.data.type }
      ];
      const sections: SectionOfDashboard[] = [];
      const sectionsData = cloneDeep(response.data.sections);
      sectionsData.forEach((section: SectionOfDashboard) => {
        const dataSection: SectionOfDashboard = {
          name: section.name,
          layout: section.layout,
          tags: section.tags,
          indicators: section.indicators,
          collections: [],
          collectionsOptions: (cloneDeep(
            this.collectionsData
          ) as unknown) as CollectionsOptions[]
        };
        section.collections.forEach(item => {
          const itemCollection = this.collectionsData.find(
            element => element.id == item.id
          );
          const dataCollection: CollectionFilter = {
            collection: [
              {
                id: item.id as string,
                name: item.name as string,
                fields: (itemCollection?.fields as unknown) as Field[]
              }
            ],
            filters: cloneDeep(item.filters),
            groupBy: cloneDeep(item.chart.groupBy),
            seeBy: item.chart.seeBy,
            rangeSize: item.chart.rangeSize,
            chartName: item.chart.chartName,
            chartType: item.chartType,
            chartColor: item.chartColor,
            query: item.query,
            layout: item.layout
          };

          dataSection.collections.push(dataCollection);
        });
        sections.push(dataSection);
      });
      store.commit(
        DASHBOARD_TEMPLATE_EVENTS.UPDATE_SECTIONS_OF_DASHBOARD,
        sections
      );
      await this.addSectionsProgressively();
      this.preloaded = true;
    }
  }

  countCollections(sections: any[]) {
    const totalFilters = sections.reduce((accSection, section) => {
      const collectionFilters =
        section.collections?.reduce(
          (accCollection: number, collection: any) => {
            return accCollection + (collection.filters?.length || 0);
          },
          0
        ) || 0;
      return accSection + collectionFilters;
    }, 0);

    totalFilters;
    store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_TOTAL_FILTERS, totalFilters);
  }

  async cleanData() {
    this.preloaded = false;
    this.dashboardTemplateName = "";
    this.state = true;
    this.sectionsOfDashboardLocalState = [];
    this.$store.commit(
      DASHBOARD_TEMPLATE_EVENTS.CLEAN_ALL_DATA_OF_DASHBOARD_TEMPLATE
    );
    if (this.dashboardTemplateId) {
      return;
    }
    await this.addSectionsProgressively();
  }

  deleteSectionOfDashboard(indexSectionOfDashboard: number) {
    store.commit(
      DASHBOARD_TEMPLATE_EVENTS.DELETE_SECTION_OF_DASHBOARD,
      indexSectionOfDashboard
    );
    this.sectionsOfDashboardLocalState.splice(indexSectionOfDashboard, 1);
  }
}
